import { useState } from 'react';
import { Link, createFileRoute, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import {
  Badge,
  ConfirmDialog,
  DataTable,
  EmptyState,
  PageBody,
  TableActionsMenu,
  buttonVariants,
  type DataTableColumn,
} from '@wundo/ui';
import type { JourneyOffsetUnit, JourneyTypeRecord } from '@wundo/shared/journey.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { deleteJourneyType, listJourneyTypes } from '@/lib/journeys.ts';

export const Route = createFileRoute('/_app/journey-types/')({
  staticData: { orgScope: 'required' },
  component: JourneyTypesPage,
});

const UNIT_SHORT: Record<JourneyOffsetUnit, string> = {
  days: 'd',
  weeks: 'w',
  months: 'mo',
};

const columns: DataTableColumn<JourneyTypeRecord>[] = [
  {
    key: 'name',
    header: 'Name',
    render: (type) => <span className='font-semibold text-ink'>{type.name}</span>,
  },
  {
    key: 'steps',
    header: 'Steps',
    render: (type) =>
      type.steps
        .map((step) => `${step.offset_amount} ${UNIT_SHORT[step.offset_unit]}`)
        .join(' · '),
  },
  {
    key: 'in_use',
    header: 'In use',
    hideBelow: 'mobile',
    render: (type) =>
      type.in_use ? <Badge tone='neutral'>Journeys follow it</Badge> : null,
  },
];

/** The active organisation's journey types: what a journey's steps and reminders follow. */
export function JourneyTypesPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { organisation } = useActiveOrganisation();
  const organisationId = organisation?.id ?? null;

  const types = useQuery({
    queryKey: ['journeyTypes', organisationId],
    queryFn: () => listJourneyTypes(organisationId!),
    enabled: organisationId !== null,
  });

  const [deleting, setDeleting] = useState<JourneyTypeRecord | null>(null);
  const [deleteError, setDeleteError] = useState('');

  const remove = useMutation({
    mutationFn: (type: JourneyTypeRecord) => deleteJourneyType(organisationId!, type.id),
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['journeyTypes', organisationId] });
    },
    onError: (failure) =>
      setDeleteError(apiErrorMessage(failure, 'Failed to delete the journey type')),
  });

  return (
    <>
      <PageHeader title='Journey types' />
      <PageBody className='flex flex-col gap-5'>
        <p className='text-base text-ink-muted'>
          The series of conversations a journey walks through, for{' '}
          {organisation?.name ?? 'this organisation'}. Edits reach journeys that are
          already running.
        </p>

        <DataTable<JourneyTypeRecord>
          label='Journey types'
          columns={columns}
          rows={types.data ?? []}
          rowKey={(type) => type.id}
          loading={organisationId === null || types.isPending}
          error={types.isError ? 'Failed to load the journey types' : undefined}
          emptyState={
            <EmptyState
              title='No journey types yet'
              description='A journey type holds the steps and reminder timing every journey of that kind follows.'
            />
          }
          toolbar={
            <Link to='/journey-types/new' className={buttonVariants({ size: 'sm' })}>
              New journey type
            </Link>
          }
          rowHref={(type) => `/journey-types/organisations/${organisationId}/${type.id}`}
          onNavigate={(href) => void router.navigate({ href })}
          actions={(type) => (
            <TableActionsMenu
              label={`Actions for ${type.name}`}
              items={[
                {
                  label: 'Edit',
                  icon: <Pencil aria-hidden='true' />,
                  onSelect: () =>
                    void router.navigate({
                      to: '/journey-types/organisations/$organisationId/$id',
                      params: { organisationId: organisationId!, id: type.id },
                    }),
                },
                {
                  label: 'Delete',
                  icon: <Trash2 aria-hidden='true' />,
                  tone: 'danger',
                  onSelect: () => {
                    setDeleteError('');
                    setDeleting(type);
                  },
                },
              ]}
            />
          )}
        />
      </PageBody>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title='Delete journey type?'
        description={`Delete “${deleting?.name ?? ''}”? A type that journeys follow cannot be deleted.`}
        confirmLabel='Delete'
        cancelLabel='Cancel'
        variant='danger'
        error={deleteError}
        onConfirm={async () => {
          if (!deleting) return;
          setDeleteError('');
          await remove.mutateAsync(deleting).catch(() => undefined);
        }}
      />
    </>
  );
}
